import { useSectionColor } from "../Shell/useSectionColor";
import { copyToClipboard } from "../../utils/common";
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
  Avatar,
  Card,
  Tabs,
  Tab,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { Event, nip19 } from "nostr-tools";
import { useRelays } from "../../hooks/useRelays";
import { fetchUserProfile } from "../../nostr";
import { DEFAULT_IMAGE_URL } from "../../utils/constants";
import Rate from "../Ratings/Rate";
import UserPollsFeed from "./UserPollsFeed";
import UserNotesFeed from "./UserNotesFeed";
import UserArticlesFeed from "./UserArticlesFeed";
import UserRatingsGiven from "./UserRatingsGiven";
import { useUserContext } from "../../hooks/useUserContext";
import { useListContext } from "../../hooks/useListContext";
import { dataLayer } from "@formstr/local-relay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MailIcon from "@mui/icons-material/Mail";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNotification } from "../../contexts/notification-context";
import { Nip05Badge } from "../Common/Nip05Badge";
import { TextWithImages } from "../Common/Parsers/TextWithImages";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GroupsIcon from "@mui/icons-material/GroupsRounded";
import GroupAddIcon from "@mui/icons-material/GroupAddRounded";
import { AddToPackDialog } from "../FollowPacks/AddToPackDialog";
import { safeSetItem } from "../../utils/localStorage";
import { FeedActionsProvider } from "../../contexts/FeedActionsContext";
import CreateFAB from "../Feed/CreateFAB";
import { ProfileEditModal } from "./ProfileEditModal";
import { NetworkFollowedBy } from "./NetworkFollowedBy";
import { ProfileListDialog } from "../Common/ProfileListDialog";
import { useFollowersPagination } from "./useFollowersPagination";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function compactNpub(npub: string): string {
  if (npub.length <= 16) return npub;
  return `${npub.slice(0, 12)}...${npub.slice(-4)}`;
}

const ProfilePage: React.FC = () => {
  const section = useSectionColor();
  const { npubOrNprofile } = useParams<{ npubOrNprofile: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pubkey, setPubkey] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addToPackOpen, setAddToPackOpen] = useState(false);
  const [followsYou, setFollowsYou] = useState(false);
  const [showContactListWarning, setShowContactListWarning] = useState(false);
  const [pendingFollowKey, setPendingFollowKey] = useState<string | null>(null);
  const [followingCount, setFollowingCount] = useState<number | null>(null);
  const [followingPubkeys, setFollowingPubkeys] = useState<string[]>([]);
  const [followingModalOpen, setFollowingModalOpen] = useState(false);
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const { relays } = useRelays();
  const { user, requestLogin } = useUserContext();
  const { followPubkey, getTrustScore, pendingFollows } = useListContext();
  const { showNotification } = useNotification();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [profileRelays, setProfileRelays] = useState<string[]>(relays);

  const {
    pubkeys: followerPubkeys,
    hasMore: hasMoreFollowers,
    loadingMore: loadingMoreFollowers,
    loadMore: loadMoreFollowers,
  } = useFollowersPagination(pubkey, followersModalOpen);

  const relaysRef = useRef(relays);
  useEffect(() => { relaysRef.current = relays; }, [relays]);

  // Relay selection (including the profile person's outbox relays) is the
  // worker's job now; the app just declares interest below.
  useEffect(() => {
    if (!pubkey) return;
    setProfileRelays(relays);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey]);

  // Auto-subscribe to following count + follows-you check (lightweight: single contact list).
  useEffect(() => {
    if (!pubkey) return;

    setFollowingCount(null);
    setFollowingPubkeys([]);
    setFollowsYou(false);

    let latestFollowingEvent: Event | null = null;

    const followingHandle = dataLayer.observe(
      [
        {
          kinds: [3],
          authors: [pubkey],
          limit: 1,
        },
      ],
      {
        onEvent: (event: Event) => {
          if (
            !latestFollowingEvent ||
            event.created_at > latestFollowingEvent.created_at
          ) {
            latestFollowingEvent = event;
            const pTags = event.tags.filter((t) => t[0] === "p");
            setFollowingCount(pTags.length);
            setFollowingPubkeys(pTags.map((t) => t[1]).filter(Boolean));
            if (user && pTags.some((t) => t[1] === user.pubkey)) {
              setFollowsYou(true);
            }
          }
        },
      },
    );

    return () => {
      followingHandle.unobserve();
    };
  }, [pubkey, profileRelays, user]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!npubOrNprofile) {
        setError("No profile identifier provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Clear the previous profile so fields the new profile doesn't set
        // (e.g. banner, website) don't visually carry over while/if the new
        // one loads or fails to fetch.
        setProfile(null);

        // Decode npub or nprofile to get pubkey
        const decoded = nip19.decode(npubOrNprofile);
        let extractedPubkey: string;

        if (decoded.type === "npub") {
          extractedPubkey = decoded.data;
        } else if (decoded.type === "nprofile") {
          extractedPubkey = decoded.data.pubkey;
        } else {
          throw new Error(
            "Invalid profile identifier. Must be npub or nprofile.",
          );
        }

        setPubkey(extractedPubkey);

        // Check the worker's store first (cache-only read) to avoid a network
        // round-trip when we already have the profile (e.g. navigating back to a
        // previously visited profile).
        const cached = await dataLayer.fetchReplaceable(0, extractedPubkey);
        if (cached) {
          // The parsed kind-0 content has no pubkey — add it so consumers
          // (e.g. the profile editor's payto fetch) can key off this profile.
          setProfile({
            ...JSON.parse(cached.content || "{}"),
            pubkey: extractedPubkey,
          });
          setLoading(false);
          return;
        }

        // Not in cache — fetch from relays using the current relay list.
        const profileEvent = await fetchUserProfile(extractedPubkey, relaysRef.current);
        if (profileEvent) {
          setProfile({
            ...JSON.parse(profileEvent.content || "{}"),
            pubkey: extractedPubkey,
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading profile:", err);
        setError(err instanceof Error ? err.message : "Failed to load profile");
        setLoading(false);
      }
    };

    loadProfile();
    // Only re-run when the profile identifier changes, not when relays change.
    // relays are read via relaysRef to avoid double-fetching on relay updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npubOrNprofile]);

  const addToContacts = async () => {
    if (!user) {
      requestLogin();
      return;
    }

    if (!pubkey) return;

    const result = await followPubkey(pubkey);
    if (result === "no-contact-list") {
      setPendingFollowKey(pubkey);
      setShowContactListWarning(true);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pubkey) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error" variant="h6">
          {error || "Failed to load profile"}
        </Typography>
      </Box>
    );
  }

  const npub = nip19.npubEncode(pubkey);
  const isOwnProfile = user?.pubkey === pubkey;
  // Trust score = how many of the viewer's own follows also follow this profile.
  // Shown on every profile (logged in, not your own) as an at-a-glance signal.
  const trustScore = user && !isOwnProfile ? getTrustScore(pubkey) : 0;

  return (
    <FeedActionsProvider>
      <Box
        ref={scrollContainerRef}
        maxWidth={800}
        mx="auto"
      sx={{
        px: { xs: 1.25, sm: 2 },
        pt: 1.25,
        pb: { xs: 12, md: 4 },
        height: "100%",
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Profile Header — Color Block: the person in focus is the block */}
      <Card
        sx={{
          mb: 2,
          overflow: "hidden",
          borderRadius: "30px",
          background: section.fill,
          color: section.on,
          "& .MuiTypography-root": { color: "inherit" },
          "& .MuiIconButton-root": { color: "inherit" },
          "& .MuiChip-root": { color: "inherit", borderColor: "currentColor" },
          "& .MuiChip-icon": { color: "inherit" },
          "& .MuiButton-outlined": { color: "inherit", borderColor: "currentColor" },
          "& .MuiButton-contained": { bgcolor: section.on, color: section.color, "&:hover": { bgcolor: section.on } },
          "& .MuiButton-text": { color: "inherit" },
        }}
      >
        {/* Banner */}
        <Box
          sx={{
            height: 120,
            m: 1,
            borderRadius: "24px",
            background: profile?.banner
              ? `url(${profile.banner}) center/cover no-repeat`
              : "linear-gradient(135deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 100%)",
          }}
        />

        {/* Identity section */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-start" },
              gap: 2,
              mt: "-40px",
            }}
          >
            {/* Avatar overlapping banner */}
            <Avatar
              src={profile?.picture || DEFAULT_IMAGE_URL}
              alt={profile?.name || "Profile"}
              sx={{
                width: 80,
                height: 80,
                border: "3px solid",
                borderColor: section.on,
                flexShrink: 0,
              }}
            />

            {/* Name, nip05, npub */}
            <Box
              sx={{
                flex: 1,
                textAlign: { xs: "center", sm: "left" },
                mt: { xs: 0, sm: "44px" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: { xs: "center", sm: "flex-start" },
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {profile?.name || profile?.username || "Unnamed"}
                </Typography>
                {followsYou && (
                  <Chip label="Follows you" size="small" variant="outlined" />
                )}
                {user && !isOwnProfile && (
                  <Tooltip
                    title={
                      trustScore > 0
                        ? `Trust score: ${trustScore} ${
                            trustScore === 1 ? "person" : "people"
                          } you follow also follow them`
                        : "No one you follow follows them"
                    }
                  >
                    <Chip
                      icon={<PeopleAltOutlinedIcon />}
                      label={
                        trustScore > 0
                          ? `${trustScore} in your network`
                          : "Not in your network"
                      }
                      size="small"
                      color={trustScore > 0 ? "primary" : "default"}
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Box>

              {profile?.nip05 && (
                <Nip05Badge nip05={profile.nip05} pubkey={pubkey} variant="body2" />
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace" }}
                >
                  {compactNpub(npub)}
                </Typography>
                <Tooltip title="Copy npub">
                  <IconButton
                    size="small"
                    onClick={() => {
                      copyToClipboard(npub);
                      showNotification("npub copied to clipboard", "success");
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            {isOwnProfile ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setEditModalOpen(true)}
                >
                  Edit Profile
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GroupsIcon />}
                  onClick={() => {
                    safeSetItem("pollerama:followPacksSource", "mine");
                    navigate("/feeds/follow-packs");
                  }}
                >
                  My packs
                </Button>
              </>
            ) : user ? (
              <>
                {user.follows?.includes(pubkey) ? (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    disableElevation
                  >
                    Following
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addToContacts}
                    disabled={pendingFollows.has(pubkey)}
                    startIcon={
                      pendingFollows.has(pubkey) ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {pendingFollows.has(pubkey)
                      ? "Following…"
                      : followsYou
                        ? "Follow Back"
                        : "Follow"}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MailIcon />}
                  onClick={() => navigate(`/messages/${npub}`)}
                >
                  Message
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GroupAddIcon />}
                  onClick={() => setAddToPackOpen(true)}
                >
                  Add to pack
                </Button>
              </>
            ) : null}
          </Box>

          {/* Bio */}
          {profile?.about && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" component="div">
                <TextWithImages content={profile.about} />
              </Typography>
            </>
          )}

          {/* Website */}
          {profile?.website && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <LinkIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="a"
                href={
                  profile.website.startsWith("http")
                    ? profile.website
                    : `https://${profile.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  minWidth: 0,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {profile.website.replace(/^https?:\/\//, "")}
              </Typography>
            </Box>
          )}

          {/* Stats + Rate */}
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 1,
              justifyContent: { xs: "center", sm: "flex-start" },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setFollowersModalOpen(true)}
            >
              <strong>
                {followerPubkeys.length > 0
                  ? `${followerPubkeys.length}${hasMoreFollowers ? "+" : ""}`
                  : "–"}
              </strong>{" "}
              followers
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                cursor: followingCount ? "pointer" : "default",
                "&:hover": followingCount
                  ? { textDecoration: "underline" }
                  : undefined,
              }}
              onClick={() => {
                if (followingCount) setFollowingModalOpen(true);
              }}
            >
              <strong>{followingCount ?? "–"}</strong> following
            </Typography>
          </Box>
          {!isOwnProfile && <NetworkFollowedBy pubkey={pubkey} />}
          <Rate entityId={pubkey} entityType="profile" />
        </Box>
      </Card>

      {/* Tabs */}
      <Box sx={{ mb: 0.5, p: 0.625, borderRadius: "18px", bgcolor: "cb.slab" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="profile tabs"
          variant="fullWidth"
          // Segmented control: the selected tab is an ink pill.
          TabIndicatorProps={{ sx: { display: "none" } }}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, borderRadius: "14px", color: "text.secondary", fontWeight: 700 },
            "& .MuiTab-root.Mui-selected": { bgcolor: "text.primary", color: "background.default" },
          }}
        >
          <Tab label="Notes" />
          <Tab label="Polls" />
          <Tab label="Articles" />
          <Tab label="Ratings" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <UserNotesFeed
          pubkey={pubkey}
          relays={profileRelays}
          scrollContainerRef={scrollContainerRef}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <UserPollsFeed
          pubkey={pubkey}
          relays={profileRelays}
          scrollContainerRef={scrollContainerRef}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <UserArticlesFeed
          pubkey={pubkey}
          relays={profileRelays}
          scrollContainerRef={scrollContainerRef}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <UserRatingsGiven
          pubkey={pubkey}
          relays={profileRelays}
          scrollContainerRef={scrollContainerRef}
        />
      </TabPanel>

      <Dialog
        open={showContactListWarning}
        onClose={() => setShowContactListWarning(false)}
      >
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Typography>
            We couldn't find your existing contact list. If you continue, your
            follow list will only contain this person.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactListWarning(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingFollowKey) {
                followPubkey(pendingFollowKey, { allowEmptyContactList: true });
              }
              setShowContactListWarning(false);
              setPendingFollowKey(null);
            }}
            color="primary"
            variant="contained"
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      {/* FAB with custom Profile actions */}
      <CreateFAB
        extraActions={
          isOwnProfile
            ? [
                {
                  icon: <EditIcon />,
                  name: "Edit Profile",
                  onClick: () => setEditModalOpen(true),
                },
              ]
            : []
        }
      />

      {user && !isOwnProfile && (
        <AddToPackDialog
          open={addToPackOpen}
          pubkey={pubkey}
          name={profile?.name || profile?.username || "them"}
          onClose={() => setAddToPackOpen(false)}
        />
      )}

      {/* Profile Edit Modal */}
      <ProfileEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        userProfile={profile}
      />

      {/* Following / Followers list modals */}
      <ProfileListDialog
        open={followingModalOpen}
        onClose={() => setFollowingModalOpen(false)}
        pubkeys={followingPubkeys}
        title="Following"
        subtitle={`${followingPubkeys.length} ${
          followingPubkeys.length === 1 ? "account" : "accounts"
        }`}
      />
      <ProfileListDialog
        open={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        pubkeys={followerPubkeys}
        title="Followers"
        subtitle={`${followerPubkeys.length}${
          hasMoreFollowers ? "+" : ""
        } ${followerPubkeys.length === 1 ? "account" : "accounts"}`}
        hasMore={hasMoreFollowers}
        loadingMore={loadingMoreFollowers}
        onLoadMore={loadMoreFollowers}
      />
    </FeedActionsProvider>
  );
};

export default ProfilePage;
